<template>
	<div class="entity-comments">
		<CommentBox
			v-if="parentId && usersLoaded"
			ref="commentBoxRef"
			:parent-id="parentId"
			:parent-type="parentType"
			:project-id="projectId"
			:project-access-denied-handled-globally="entityType === 'project'"
			:load-project-shares="canLoadProjectShares"
			:is-view-only="isViewOnly || !isAuthenticated || hasFreeTrialEnded"
			:light-box-view="false"
			:users="users"
			:hide-header="true"
			:me="me"
			@show-lucky="showLucky"
			@hide-lucky="hideLucky" />
		<div v-else class="pa-4 text-center w-100">
			<v-progress-circular indeterminate color="primary"></v-progress-circular>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onBeforeUnmount, watch, ref, computed } from "vue";
	import CommentBox from "@/components/CommentBox.vue";
	import type { Conversation } from "@/types/Conversation";
	import type { Project } from "@/types/Project";
	import type { EntityType } from "@/types/EntityType";
	import api from "@/api";
	import { loadProjectShareRoster, viewerOrganizationOwnsProject } from "@/helpers/projectShareRoster";
	import { isRoleOrInRoles } from "../../../api/account";
	import { Role } from "../../../enums/Role";

	const emit = defineEmits(["show-lucky", "hide-lucky"]);

	const props = defineProps<{
		entity: Conversation | Project;
		entityType: EntityType;
		isViewOnly: boolean;
		isAuthenticated: boolean;
		hasFreeTrialEnded: boolean;
	}>();

	const me = ref<any>(null);
	const users = ref<any[]>([]);
	const usersLoaded = ref(false);
	const commentBoxRef = ref<InstanceType<typeof CommentBox> | null>(null);

	const parentId = computed(() => (props.entity as any)?.id ?? "");
	const parentType = computed(() => props.entityType);
	const projectId = computed(() => {
		if (props.entityType === "project") {
			return (props.entity as Project)?.id ?? null;
		}
		const conv = props.entity as Conversation;
		return (conv as any)?.Project?.id ?? null;
	});

	async function loadMe() {
		try {
			me.value = await (api as any).getMe();
		} catch (err) {
			console.error(err);
			me.value = null;
		}
	}

	async function loadUsers() {
		usersLoaded.value = false;
		try {
			if (!parentId.value) {
				users.value = [];
				usersLoaded.value = true;
				return;
			}

			if (props.entityType === "project") {
				if (!canLoadProjectShares.value) {
					// LSP-20532: an external collaborator (viewer from another organization) has no access to
					// the owner-scoped roster route, so do not issue a request the API will refuse with 404.
					users.value = [];
					return;
				}
				const result = await loadProjectShareRoster(parentId.value, { pageSize: 1000 });
				if (isUnmounted) return;
				const members = (result?.rows || []).filter((x: any) => !!x?.User).map((x: any) => x.User);
				// Ensure unique users by id
				const uniqueById = new Map<string, any>();
				for (const u of members) {
					if (!uniqueById.has(u.id)) uniqueById.set(u.id, u);
				}
				users.value = Array.from(uniqueById.values());
			} else if (props.entityType === "conversation") {
				const conv = props.entity as Conversation & { forcedUserId?: string };
				const result = await (api as any).getUsers({ page: 1, pageSize: 1000 });
				if (isUnmounted) return;
				const rows: any[] = result?.rows || [];
				users.value = rows.filter((user: any) => {
					const roleName = user?.Account?.Roles?.[0]?.name;
					return conv?.forcedUserId === user?.id || isRoleOrInRoles(roleName, [Role.Administrator, Role.SuperAdministrator]);
				});
				if (users.value.length === 0) {
					users.value = rows;
				}
			} else {
				users.value = [];
			}
		} catch (err) {
			console.error(err);
			users.value = [];
		} finally {
			usersLoaded.value = true;
		}
	}

	// LSP-20532: true when the signed-in viewer's organization owns the project (fails open while either
	// id is unknown). Only meaningful for project entities; conversations never hit the roster route.
	const canLoadProjectShares = computed(() => {
		if (props.entityType !== "project") return true;
		const projectOrganizationId = (props.entity as Project & { organizationId?: string })?.organizationId;
		return viewerOrganizationOwnsProject(me.value?.organization?.id, projectOrganizationId);
	});

	// Review finding: the org gate makes onMounted await `me` before the roster, so a panel closed during
	// getMe must not go on to mutate state or issue the roster request on a torn-down instance.
	let isUnmounted = false;
	onBeforeUnmount(() => {
		isUnmounted = true;
	});

	onMounted(async () => {
		// The org gate needs `me`, so resolve it before the roster instead of in parallel.
		await loadMe();
		if (isUnmounted) {
			console.warn("EntityComments: unmounted while loading the current user; skipping the roster load", {
				entityType: props.entityType,
				parentId: parentId.value
			});
			return;
		}
		await loadUsers();
	});

	watch(
		() => [props.entityType, (props.entity as any)?.id],
		async () => {
			await loadUsers();
		}
	);

	function showLucky() {
		emit("show-lucky");
	}

	function hideLucky() {
		emit("hide-lucky");
	}

	function hasUnsavedComment(): boolean {
		return Boolean(commentBoxRef.value?.hasEditedComment || commentBoxRef.value?.hasUnsentComment);
	}

	async function confirmLeaveWithUnsavedComment(): Promise<boolean> {
		return (await commentBoxRef.value?.confirmLeaveWithUnsavedComment?.()) ?? true;
	}

	defineExpose({ hasUnsavedComment, confirmLeaveWithUnsavedComment });
</script>

<style lang="scss" scoped>
	.entity-comments {
		width: 100%;
		display: flex;
		flex-direction: column;
		max-height: calc(100vh - 220px);
		overflow: hidden;

		@media #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: portrait) {
			max-height: calc(100vh - 420px);
		}

		// LSP-16646: landscape previously fell back to the desktop reservation
		// (100vh - 220px). On a short landscape viewport that left too little
		// height, so the flex:1 message list collapsed to ~0 and the natural-height
		// composer filled the section, hiding the conversation. Reserve far less in
		// landscape so the list keeps usable, scrollable height above the composer.
		@media #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: landscape) {
			max-height: calc(100vh - 140px);
		}
	}

	.entity-comments :deep(.comment-box) {
		display: flex;
		flex-direction: column;
		overflow: hidden;
		flex: 1;
		min-height: 0;
	}

	.entity-comments :deep(.comments-container) {
		overflow-y: auto;
		scrollbar-width: thin;
		flex: 1;
		min-height: 0;
	}
</style>
